import PaneContainer from '@/layouts/components/PaneContainer'
import SingleColumn from '@/layouts/SingleColumn'
import { CsvImportResponse, CsvService } from '@/api'
import { toastAtom } from '@/atoms/toastAtom'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import { Button } from 'primereact/button'
import { ProgressBar } from 'primereact/progressbar'
import { Dialog } from 'primereact/dialog'
import { useMutationTaskWithProgress } from '@/api/hooks/useTaskWithProgress'
import { Panel } from 'primereact/panel'
import { Link } from '@tanstack/react-router'

const ImportCsv = () => {
  const toast = useAtomValue(toastAtom)
  const queryClient = useQueryClient()
  const [files, setFiles] = useState<File[]>([])
  const [errors, setErrors] = useState<string[]>([])
  const [lockDialog, setLockDialog] = useState<{ visible: boolean; message?: string }>({ visible: false })
  const [reopenDialog, setReopenDialog] = useState(false)
  const [ignoreListText, setIgnoreListText] = useState('')

  type IgnoreEntry = { sheet: string; part: string; location: string; date?: string }

  const formatIgnoreList = (items: IgnoreEntry[]) =>
    items
      .map((item) =>
        item.date
          ? `${item.sheet}, ${item.part}, ${item.location}, ${item.date}`
          : `${item.sheet}, ${item.part}, ${item.location}`,
      )
      .join('\n')

  const parseIgnoreList = (text: string): IgnoreEntry[] =>
    text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => line.split(/[\t,，]/).map((cell) => cell.trim()).filter(Boolean))
      .filter((cells) => cells.length >= 3)
      .map(([sheet, part, location, date]) =>
        date ? { sheet, part, location, date } : { sheet, part, location },
      )

  useEffect(() => {
    let active = true
    const loadIgnoreList = async () => {
      try {
        const res = await fetch('/api/csv/ignore-list')
        if (!res.ok) return
        const data = await res.json()
        if (!Array.isArray(data)) return
        const entries = data
          .filter((item) => item && typeof item === 'object')
          .map((item) => {
            const date = String((item as any).date ?? '').trim()
            return {
              sheet: String((item as any).sheet ?? '').trim(),
              part: String((item as any).part ?? '').trim(),
              location: String((item as any).location ?? '').trim(),
              ...(date ? { date } : {}),
            }
          })
          .filter((item) => item.sheet && item.part && item.location)
        if (active) {
          setIgnoreListText(formatIgnoreList(entries))
        }
      } catch {
        // ignore fetch errors and keep local input empty
      }
    }

    loadIgnoreList()
    return () => {
      active = false
    }
  }, [])

  const extractErrors = (res: unknown): string[] => {
    const errs = (res as any)?.errors
    return Array.isArray(errs) ? errs : []
  }

  const {
    mutateAsync: upload,
    progress,
    lastLog,
    logs,
    isObserving,
  } = useMutationTaskWithProgress(CsvService.csvImport, {
    onSuccess: (res) => {
      // handled in handleUpload to accumulate per file
      queryClient.invalidateQueries({ queryKey: ['selectedFile'] })
      if(!res){
        return
      }
      const res2 = res as CsvImportResponse
      toast?.show({
        severity: 'success',
        summary: 'アップロード完了',
        detail: `CSVを取り込みました（行数: ${res2.rows_imported}${res2.worksheet_name ? `, シート: ${res2.worksheet_name}` : ''}）`,
        life: 3000,
      })
      setReopenDialog(true)
    },
    onError: (err: unknown) => {
      // handled in handleUpload to accumulate per file
      const message = err instanceof Error ? err.message : 'アップロードに失敗しました'
      if (message && message.includes('423')) {
        setLockDialog({ visible: true, message: 'Excelが開かれている可能性があります。Excelを閉じてから再実行してください。' })
      }
      toast?.show({
        severity: 'error',
        summary: 'エラー',
        detail: message,
        life: 4000,
      })
    },
  })

  const fileHint = useMemo(() => {
    if (!files.length) return 'CSVファイルを選択してください'
    const names = files.map((f) => f.name).join(', ')
    const sizeKb = Math.round(files.reduce((acc, f) => acc + f.size, 0) / 1024)
    return `${names} / 合計約${sizeKb}KB`
  }, [files])

  const handleUpload = async () => {
    if (!files.length) return
    setErrors([])
    const ignoreList = parseIgnoreList(ignoreListText)
    const ignoreListPayload = ignoreListText.trim() ? ignoreList : undefined
    const payload = files.length === 1
      ? ({ file1: files[0], ignore_list: ignoreListPayload } as any)
      : files.length === 2
        ? ({ file1: files[0], file2: files[1] ?? null, ignore_list: ignoreListPayload } as any)
        : files.length === 3
          ? ({ file1: files[0], file2: files[1] ?? null, file3: files[2] ?? null, ignore_list: ignoreListPayload } as any)
          : ({ file1: files[0], file2: files[1] ?? null, file3: files[2] ?? null, file4: files[3] ?? null, ignore_list: ignoreListPayload } as any)
    try {
      const res = await upload([payload])
      const newErrs = extractErrors(res)
      if (newErrs.length) {
        setErrors(newErrs)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'アップロードに失敗しました'
      setErrors([message])
    }
  }

  return (
    <SingleColumn>
      <PaneContainer>
        <div className="tm-grid tm-grid-cols-1 gap-4 w-full tw-mx-2 mx-auto">
          <Panel className="flex flex-column gap-4 w-full mx-auto"
            header="CSV取り込み"
          >
            <div className="flex flex-column gap-4">
              <div>
                <Link to="/dashboard">&lt; ダッシュボードに戻る</Link>
              </div>
              <p className="text-700 mb-3">
                CSVファイルをアップロードし、サーバー側でOneDriveで選択中のExcelファイルにデータを転記します。
                <ul>
                  <li>対応ファイルはTST/TWT/確定用のCSVファイルで、最大4件までアップロード可能です。
                  </li>
                  <li>「アップロードして取り込む」ボタンを押す前にExcelファイルを閉じてください。</li>
                  <li>取り込み完了後、Excelで該当のファイルを開いて「上書き保存」を行い、数式を再計算してください。</li>
                </ul>
              </p>

              <div className="flex flex-column gap-3">
                <div className="flex flex-column gap-2">
                  <label className="font-medium">CSVファイル（TST/TWT/確定用 最大4件）</label>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    multiple
                    onChange={(e) => setFiles(e.target.files ? Array.from(e.target.files).slice(0, 4) : [])}
                    disabled={isObserving}
                  />
                  <small className="text-600">{fileHint}</small>
                </div>
                <div className="flex flex-column gap-2">
                  <label className="font-medium">無視リスト（任意）</label>
                  <textarea
                    className="p-inputtext w-full"
                    rows={5}
                    placeholder={'例: 450AD, D42C17, TWT茨城\n例: 450AD, D42C58Z, TWT茨城, 2026/08/24'}
                    value={ignoreListText}
                    onChange={(e) => setIgnoreListText(e.target.value)}
                    disabled={isObserving}
                  />
                  <small className="text-600">
                    1行ごとに「シート名, 型番, 納入拠点」を入力してください。4列目に日付（例: 2026/08/24）を付けると、その日付以降（当日含む）の納期のみを無視します。日付を省略すると従来通り全期間を無視します。空の場合は前回保存された無視リストを使用します。
                  </small>
                </div>
                <div className="flex align-items-center gap-2">
                  <Button
                    label="アップロードして取り込む"
                    icon="pi pi-upload"
                    severity="success"
                    onClick={handleUpload}
                    disabled={!files.length || isObserving}
                    loading={isObserving}
                  />
                  <Button
                    label="クリア"
                    icon="pi pi-times"
                    outlined
                    onClick={() => setFiles([])}
                    disabled={isObserving || !files.length}
                  />
                </div>
                <div className="flex flex-column gap-2">
                  <div className="flex align-items-center gap-2">
                    <i className="pi pi-exclamation-circle text-600" aria-hidden />
                    <span className="font-medium">インポート結果</span>
                  </div>
                  <div
                    className="surface-50 border-200 border-1 p-3"
                    style={{ maxHeight: '220px', overflowY: 'auto' }}
                  >
                    {errors.length === 0 ? (
                      <span className="text-600">エラーはありません</span>
                    ) : (
                      <ul className="m-0 pl-3 text-800">
                        {errors.map((err, idx) => (
                          <li key={`${idx}-${err.slice(0, 20)}`}>{err}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                {isObserving && (
                  <div className="flex flex-column gap-2">
                    <ProgressBar
                      value={progress?.toFixed(2) ?? undefined}
                      mode={progress === null ? 'indeterminate' : 'determinate'}
                      style={{ height: '1rem' }}
                    />
                    <small className="text-600">{lastLog || '処理中...'}</small>
                  </div>
                )}
              </div>
            </div>
          </Panel>
        </div>
        <Dialog
          header="Excelを閉じてください"
          visible={lockDialog.visible}
          style={{ width: '32rem' }}
          modal
          onHide={() => setLockDialog({ visible: false })}
          footer={
            <div className="flex justify-content-end gap-2">
              <Button
                label="OK"
                onClick={() => setLockDialog({ visible: false })}
                autoFocus
              />
            </div>
          }
        >
          <p className="m-0 text-800">{lockDialog.message || 'Excelが開かれているため処理できません。Excelを閉じてから再実行してください。'}</p>
        </Dialog>
        <Dialog
          header="Excelを開いて保存し直してください"
          visible={reopenDialog}
          style={{ width: '32rem' }}
          modal
          onHide={() => setReopenDialog(false)}
          footer={(
            <div className="flex justify-content-end gap-2">
              <Button
                label="OK"
                onClick={() => setReopenDialog(false)}
                autoFocus
              />
            </div>
          )}
        >
          <p className="m-0 text-800">
            取り込みが完了しました。Excelで該当のファイルを開いて「上書き保存」を行い、数式を再計算してください。
          </p>
        </Dialog>
      </PaneContainer>
    </SingleColumn>
  )
}

export default ImportCsv
